import { getQrCode as getQrCodeApi, getQrCodeStatus as getQrCodeStatusApi } from '@/api/wechat'
import { usePool } from '@/utils/usePool.js'
import { useTimeout } from '@/utils/useTimeout.js'
import { ref } from 'vue'

export const QrCodeState = {
  // 客户端状态（不涉及服务端状态码）
  IDLE: 'idle', // 初始态
  GENERATING: 'generating', // 二维码生成中
  GENERATE_FAILED: 'generate_failed', // 二维码获取失败
  UNKONWN: 'unknown', // 未知状态

  // 服务端状态码
  UNKONWN_ERROR: -1, // 未知错误
  SCAN_SUCCESS: 1, // 扫码成功
  SYSTEM_ERROR: 20001, // 系统错误
  QR_GENERATED: 20101, // 初始态，二维码已生成
  GET_UNIONID_FAILED: 20103, // 获取UnioID失败
  WECHAT_BOUND_TO_OTHER_USER: 20104, // 当前微信已绑定其它车务用户
  USER_BOUND_TO_OTHER_WECHAT: 20105, // 当前车务用户已绑定其它微信
  QR_EXPIRED: 20106, // 二维码已过期
}

// 状态码到QrCodeState的映射
export const CODE_TO_STATE_MAP = {
  1: QrCodeState.SCAN_SUCCESS,
  20101: QrCodeState.QR_GENERATED,
  20103: QrCodeState.GET_UNIONID_FAILED,
  20104: QrCodeState.WECHAT_BOUND_TO_OTHER_USER,
  20105: QrCodeState.USER_BOUND_TO_OTHER_WECHAT,
  20106: QrCodeState.QR_EXPIRED,
  [-1]: QrCodeState.UNKONWN_ERROR,
}

export function useQrCode(options = {}) {
  const { sceneId, params, customErrorCodes, interval = 1000, timeout = 5 * 60 * 1000 } = options

  const qrCodeId = ref('')
  const qrCode = ref('')
  const state = ref(QrCodeState.IDLE)
  const eventId = ref('')
  const errorMessage = ref('')
  let stopPoolingFn = null
  let stopTimeoutFn = null
  function dispose() {
    if (stopPoolingFn) {
      stopPoolingFn()
    }
    if (stopTimeoutFn) {
      stopTimeoutFn()
    }
    reset()
  }

  // 已知业务code
  const defaultCustomErrorCodes = [
    QrCodeState.QR_GENERATED,
    QrCodeState.GET_UNIONID_FAILED,
    QrCodeState.WECHAT_BOUND_TO_OTHER_USER,
    QrCodeState.USER_BOUND_TO_OTHER_WECHAT,
    QrCodeState.QR_EXPIRED,
    QrCodeState.UNKONWN_ERROR,
  ]

  const reset = () => {
    qrCodeId.value = ''
    qrCode.value = ''
    state.value = QrCodeState.IDLE
    stopPoolingFn = null
    stopTimeoutFn = null
  }

  const init = async () => {
    // 1. 获取二维码
    await getQrCode()
    // 2. 开始轮询
    const { isActive, pause: stop } = usePool(async () => {
      stopPoolingFn = stop
      await getStatus(stop)
    }, interval)
    // 3. 启动客户端倒计时
    const { stop: stopTimeout } = useTimeout(() => {
      stopTimeoutFn = stopTimeout
      if (isActive.value) { // 到达客户端阈值后，如果轮询还在进行，则强制停止，二维码置为已过期
        stop()
        state.value = QrCodeState.QR_EXPIRED
      }
    }, timeout + 5000) // 加5s钟的修正，防止和服务端有延时
  }

  const getQrCode = async () => {
    try {
      const { code, data } = await getQrCodeApi({
        sceneId,
        params,
      })

      if (code === 1 && data) {
        qrCodeId.value = data.key
        qrCode.value = data.qrCode
        state.value = QrCodeState.QR_GENERATED
      } else {
        state.value = QrCodeState.GENERATE_FAILED
      }
    } catch (error) {
      state.value = QrCodeState.GENERATE_FAILED
      throw error
    }
  }

  const getStatus = async (stop) => {
    try {
      const { code, msg } = await getQrCodeStatusApi({
        key: qrCodeId.value,
      }, [...defaultCustomErrorCodes, ...customErrorCodes])

      state.value = CODE_TO_STATE_MAP[code]
      errorMessage.value = msg

      // 如果扫码成功，停止轮询
      if (state.value === QrCodeState.SCAN_SUCCESS) {
        stop()
      }
    } catch (error) {
      // 只处理业务类型错误，其他类型error catch但不处理
      if (error && typeof error === 'object' && 'code' in error && 'data' in error) {
        const { code, data, msg } = error
        const newState = CODE_TO_STATE_MAP[code]
        eventId.value = data?.eventId || ''
        errorMessage.value = msg || ''

        if (!newState) {
          state.value = QrCodeState.UNKONWN
        } else {
          state.value = newState
        }

        if (newState !== QrCodeState.QR_GENERATED) {
          stop()
        }
      }
    }
  }

  // 刷新二维码
  const refresh = () => {
    reset()
    init()
  }

  init()

  return {
    qrCode,
    state,
    eventId,
    errorMessage,
    refresh,
    dispose,
  }
}
